import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import { useLanguage } from "../../contexts/LanguageContext";
import { analyticsApi } from "../../services/analyticsApi";
import { apiErrorMessage } from "../../utils/apiErrorMessage";
import DataTable from "../charts/DataTable";
import { LOCALES, formatCount } from "../charts/format";
import useHideOnEscape from "../charts/useHideOnEscape";
import InlineBar from "./InlineBar";
import KpiStrip from "./KpiStrip";
import { countText, startedWithSuggestionKpi } from "./kpis";
import Section from "./Section";

// O separador Conteudo (spec do redesign, R7; decisao 12, pontos 8 e 9 do spec principal): as sugestoes
// por contexto, com uma barra em cada linha e as nunca usadas assinaladas, e as perguntas escritas mais
// frequentes, as dez primeiras e o resto a pedido. Uma sugestao acima do teto diz "nao medida" e nunca
// "nunca usada" (D13 da entrega 6). A primeira pagina vem da pagina (plano, D6); aqui so se pedem as
// seguintes, e o servidor diz onde continuar (o proximo pedido segue o offset dele, nao o tamanho da lista).
const secondary = { color: "var(--color-text-secondary)" };
const card = { borderColor: "var(--color-border)", backgroundColor: "var(--color-surface)" };
const TYPED_SHOWN = 10;

// Um botao por linha que abre as ligacoes num popover por baixo dele, em vez de as abrir dentro da linha
// (que a esticava e empurrava as outras). So um aberto de cada vez: o `openKey` vive no ContentTab, o que
// cobre tambem a abertura ao teclado, que nao tem mousedown. O popover desenha-se num portal no body, com
// `position: fixed`, porque o `overflow-x-auto` da tabela corta tambem na vertical e cortava o das ultimas
// linhas. Como fixo nao acompanha a linha, fecha com o scroll (da janela ou do <main>) e com o resize.
// Fecha tambem com Escape, com um mousedown fora, como o CustomPeriod, ao escolher uma ligacao e com o Tab
// para fora das ligacoes; nestes casos, e quando o foco estava dentro, o foco volta ao botao. Os mais
// recentes primeiro, venha a lista pela ordem que vier.
// Fica abaixo da barra fixa da pagina (4 contra o 5 dela), que marca `data-sticky-bar`: o espaco por
// cima do botao mede-se a partir do fundo da barra, e a lista encolhe para o espaco do lado escolhido.
const GAP = 4;
const MARGIN = 8;
const MAX_LIST_HEIGHT = 192;
const ConversationLinks = ({ conversations, popoverKey, openKey, setOpenKey }) => {
  const { translate, language } = useLanguage();
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);
  const [position, setPosition] = useState(null);
  const id = useId();
  const open = openKey === popoverKey;
  const close = useCallback(() => setOpenKey(null), [setOpenKey]);
  const closeToButton = useCallback(() => {
    setOpenKey(null);
    buttonRef.current?.focus({ preventScroll: true });
  }, [setOpenKey]);
  useHideOnEscape(open, closeToButton);

  // Por baixo do botao, ou por cima se nao coubesse; se nenhum lado chega, o lado com mais espaco, com a
  // lista encolhida. Alinhado a esquerda do botao e a 8px das margens visiveis do <main> (da janela, sem
  // <main>), com a largura medida do popover.
  useLayoutEffect(() => {
    if (!open) return;
    const button = buttonRef.current.getBoundingClientRect();
    const popover = popoverRef.current.getBoundingClientRect();
    const list = popoverRef.current.querySelector("ul").getBoundingClientRect();
    const main = buttonRef.current.closest("main");
    const mainBox = main?.getBoundingClientRect();
    const minLeft = (mainBox ? mainBox.left + main.clientLeft : 0) + MARGIN;
    const right = mainBox ? mainBox.left + main.clientLeft + main.clientWidth : document.documentElement.clientWidth;
    const left = Math.max(minLeft, Math.min(button.left, right - MARGIN - popover.width));
    const viewportBottom = document.documentElement.clientHeight;
    const barBottom = (main?.querySelector("[data-sticky-bar]") ?? main)?.getBoundingClientRect().bottom ?? 0;
    const roomBelow = viewportBottom - MARGIN - (button.bottom + GAP);
    const roomAbove = button.top - GAP - MARGIN - barBottom;
    const below = popover.height <= roomBelow || roomBelow >= roomAbove;
    const room = below ? roomBelow : roomAbove;
    const listMaxHeight = Math.max(0, Math.min(MAX_LIST_HEIGHT, list.height + room - popover.height));
    setPosition(below ? { left, top: button.bottom + GAP, listMaxHeight } : { left, bottom: viewportBottom - button.top + GAP, listMaxHeight });
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return undefined;
    }
    const onPointerDown = (event) => {
      if (!wrapperRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) close();
    };
    const closeFromViewport = () => (popoverRef.current?.contains(document.activeElement) ? closeToButton() : close());
    // O scroll da propria lista do popover nao o fecha.
    const onScroll = (event) => {
      if (!popoverRef.current?.contains(event.target)) closeFromViewport();
    };
    document.addEventListener("mousedown", onPointerDown);
    window.addEventListener("scroll", onScroll, { capture: true });
    window.addEventListener("resize", closeFromViewport);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", closeFromViewport);
    };
  }, [open, close, closeToButton]);

  // O foco so passa para a primeira ligacao depois de o popover ficar visivel: um elemento com
  // visibility hidden nao aceita foco, e na primeira pintura ele espera pela posicao medida.
  useEffect(() => {
    if (open && position) popoverRef.current.querySelector("a")?.focus({ preventScroll: true });
  }, [open, position]);

  // O Tab para la da ultima ligacao (ou o Shift+Tab da primeira) fecha o popover e deixa o foco no botao,
  // para o Tab seguinte continuar na linha seguinte como de costume.
  const onKeyDown = (event) => {
    if (event.key !== "Tab") return;
    const links = popoverRef.current.querySelectorAll("a");
    const edge = event.shiftKey ? links[0] : links[links.length - 1];
    if (document.activeElement === edge) {
      event.preventDefault();
      closeToButton();
    }
  };

  if (!conversations || conversations.length === 0) return null;
  const locale = LOCALES[language] || language;
  const newestFirst = [...conversations].sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  return (
    <span ref={wrapperRef} className="inline-block">
      <button
        ref={buttonRef}
        type="button"
        aria-label={translate("analyticsOpenConversations", { count: conversations.length })}
        aria-expanded={open}
        aria-controls={open ? id : undefined}
        onClick={() => setOpenKey(open ? null : popoverKey)}
        className="inline-flex items-center justify-center gap-1 text-xs rounded whitespace-nowrap p-1 min-w-[24px] min-h-[24px] sm:p-0 sm:min-w-0 sm:min-h-0"
        style={{ color: "var(--color-text-secondary)" }}
      >
        <MessageSquare className="w-3.5 h-3.5" aria-hidden="true" />
        <span className="hidden sm:inline">{translate("analyticsViewConversations")}</span>
      </button>
      {open &&
        createPortal(
          <div
            id={id}
            ref={popoverRef}
            onKeyDown={onKeyDown}
            className="fixed z-[4] w-60 max-w-[calc(100vw-3rem)] rounded-lg border p-2 text-xs font-normal text-left"
            style={{ left: position?.left, top: position?.top, bottom: position?.bottom, visibility: position ? "visible" : "hidden", backgroundColor: "var(--color-surface-raised)", borderColor: "var(--color-border)", color: "var(--color-text-primary)" }}
          >
            <p id={`${id}-title`} className="mb-1 font-semibold">
              {newestFirst.length === 1 ? translate("analyticsRecentConversation") : translate("analyticsRecentConversations", { count: newestFirst.length })}
            </p>
            <ul aria-labelledby={`${id}-title`} className="overflow-y-auto space-y-0.5" style={{ maxHeight: position ? position.listMaxHeight : MAX_LIST_HEIGHT }}>
              {newestFirst.map((conversation) => (
                <li key={conversation.id}>
                  <Link to={`/conversations/${conversation.id}`} onClick={close} className="underline">
                    {new Date(conversation.started_at).toLocaleString(locale, { dateStyle: "short", timeStyle: "short" })}
                  </Link>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
    </span>
  );
};

// O esqueleto do separador enquanto a primeira pagina vem a caminho, em vez de "A carregar" (spec do
// redesign, R8): as sugestoes e as perguntas. O `relative` segura o sr-only dentro do <main>.
const Skeleton = () => {
  const { translate } = useLanguage();
  const block = (className) => <div className={`rounded-xl animate-pulse ${className}`} style={{ backgroundColor: "var(--color-surface-raised)" }} />;
  return (
    <div aria-busy="true" className="relative space-y-6">
      <span className="sr-only">{translate("loading")}</span>
      {block("h-48")}
      {block("h-64")}
    </div>
  );
};

const contextName = (context, translate) => {
  if (context.context === "default") return translate("analyticsWelcomeContext");
  if (context.status === "deleted") return translate("analyticsDeletedContext");
  return context.name || translate("analyticsUnnamedContext");
};

// A sugestao mais usada e as nunca usadas, de todos os contextos (E7): so existem quando a primeira
// pagina ja os traz todos, senao seria o numero de uma parte dado como o do todo. As duas contam so as
// configuradas: uma sugestao que ja nao esta configurada nunca e a mais usada, e as nunca usadas sao as
// configuradas e medidas sem usos, a mesma regra da etiqueta "Nunca usada" da tabela.
const suggestionTotals = (suggestions) => {
  if (suggestions.contexts_total > suggestions.contexts.length) return null;
  const configured = suggestions.contexts.flatMap((context) => context.suggestions).filter((suggestion) => suggestion.configured && suggestion.measured);
  const most = configured.reduce((best, suggestion) => (best === null || suggestion.uses > best.uses ? suggestion : best), null);
  return {
    most: most && most.uses > 0 ? most : null,
    neverUsed: configured.filter((suggestion) => suggestion.uses === 0).length,
  };
};

// A faixa do separador. As conversas iniciadas por uma sugestao sao as da visao geral (`kpis.js`), do
// resumo que a pagina ja tem; as outras vem da primeira pagina e, enquanto ela carrega (`first` a null),
// mostram um esqueleto. A pergunta escrita mais frequente e a primeira da lista, que o backend ordena
// pelas conversas: o tamanho da lista e o teto dele (20), e nao diz nada.
const ContentStrip = ({ summary, first, previousComplete }) => {
  const { translate, language } = useLanguage();
  const count = (value) => formatCount(value, language);
  const pending = <span aria-hidden="true" className="inline-block h-8 w-16 rounded animate-pulse align-middle" style={{ backgroundColor: "var(--color-surface-raised)" }} />;
  const totals = first ? suggestionTotals(first.suggestions) : null;
  const top = first?.typed_questions.items[0];
  const kpis = [
    startedWithSuggestionKpi({ summary, previousComplete, translate, language }),
    {
      key: "typed",
      label: translate("analyticsMostTypedQuestion"),
      value: !first ? pending : top ? top.text : translate("analyticsNotAvailable"),
      context: top ? countText(top.conversation_count, "analyticsKpiConversationCount", translate, language) : null,
      textValue: true,
    },
  ];
  if (!first || totals) {
    const most = totals?.most;
    kpis.push(
      {
        key: "most_used",
        label: translate("analyticsMostUsedSuggestion"),
        value: !first ? pending : most ? most.label : translate("analyticsNotAvailable"),
        context: most ? translate(most.uses === 1 ? "analyticsKpiUsesOne" : "analyticsKpiUses", { count: count(most.uses) }) : null,
        textValue: true,
      },
      {
        key: "never_used",
        label: translate("analyticsNeverUsedSuggestions"),
        value: !first ? pending : count(totals.neverUsed),
        attention: Boolean(totals) && totals.neverUsed > 0,
      }
    );
  }
  return <KpiStrip label={translate("analyticsHeadline")} kpis={kpis} />;
};

// A pagina monta este componente de novo, com uma key nova, quando chega a primeira pagina: a semente de contexts e nextOffset e sempre a primeira pagina atual.
const ContentTab = ({ summary, first, error, onRetry, chatId, period, canOpenConversations, previousComplete }) => {
  const { translate, language } = useLanguage();
  const [contexts, setContexts] = useState(first ? first.suggestions.contexts : []);
  const [nextOffset, setNextOffset] = useState(first ? first.suggestions.contexts.length : 0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(null);
  const [allTyped, setAllTyped] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  if (!first) {
    return error ? (
      <div role="alert" className="text-sm space-y-2" style={{ color: "var(--color-error-text)" }}>
        <p>{apiErrorMessage(error, translate("analyticsLoadFailed"))}</p>
        <button type="button" onClick={onRetry} className="px-3 py-1.5 text-sm rounded-lg border" style={{ ...card, color: "var(--color-text-primary)" }}>
          {translate("analyticsRetry")}
        </button>
      </div>
    ) : (
      <div className="space-y-6">
        <ContentStrip summary={summary} first={null} previousComplete={previousComplete} />
        <Skeleton />
      </div>
    );
  }

  const { suggestions, typed_questions: typed } = first;

  const loadMore = async () => {
    setLoadingMore(true);
    setMoreError(null);
    try {
      const next = await analyticsApi.firstQuestions(chatId, period, { offset: nextOffset, limit: suggestions.limit });
      setNextOffset(nextOffset + next.suggestions.contexts.length);
      setContexts((shown) => {
        const shownKeys = new Set(shown.map((context) => context.context));
        return [...shown, ...next.suggestions.contexts.filter((context) => !shownKeys.has(context.context))];
      });
    } catch (err) {
      setMoreError(err);
    } finally {
      setLoadingMore(false);
    }
  };

  const links = canOpenConversations ? [{ key: "links", label: translate("analyticsLinks") }] : [];
  const typedItems = allTyped ? typed.items : typed.items.slice(0, TYPED_SHOWN);
  const typedMax = Math.max(0, ...typed.items.map((item) => item.conversation_count));

  return (
    <div className="space-y-6">
      <ContentStrip summary={summary} first={first} previousComplete={previousComplete} />
      <Section title={translate("analyticsSuggestions")} info={translate("analyticsSuggestionsNote")} className="space-y-4">
        {suggestions.uses_unmeasured > 0 && (
          <p className="text-xs" style={secondary}>
            {translate("analyticsNotMeasuredNote", { count: formatCount(suggestions.uses_unmeasured, language) })}
          </p>
        )}
        {contexts.length === 0 && (
          <p className="text-sm" style={secondary}>
            {translate("analyticsNoData")}
          </p>
        )}
        {contexts.map((context) => {
          const max = Math.max(0, ...context.suggestions.map((suggestion) => (suggestion.measured ? suggestion.uses : 0)));
          return (
            <div key={context.context} className="rounded-xl border p-4" style={card}>
              <h3 className="text-sm font-semibold mb-2" style={{ color: "var(--color-text-primary)" }}>
                {contextName(context, translate)} <span className="font-normal" style={secondary}>{formatCount(context.uses, language)}</span>
              </h3>
              <DataTable
                caption={contextName(context, translate)}
                columns={[{ key: "label", label: translate("analyticsSuggestion") }, { key: "uses", label: translate("analyticsUses"), numeric: true }, ...links]}
                rows={context.suggestions.map((suggestion) => ({
                  key: suggestion.label,
                  label: (
                    <span className="inline-flex flex-wrap items-center gap-2">
                      {suggestion.configured ? suggestion.label : `${suggestion.label} (${translate("analyticsNoLongerConfigured")})`}
                      {suggestion.configured && suggestion.measured && suggestion.uses === 0 && (
                        <span className="text-[11px] px-1.5 py-px rounded" style={{ backgroundColor: "var(--color-delta-bad-bg)", color: "var(--color-delta-bad-text)" }}>
                          {translate("analyticsNeverUsed")}
                        </span>
                      )}
                    </span>
                  ),
                  uses: suggestion.measured ? <InlineBar value={suggestion.uses} max={max} text={formatCount(suggestion.uses, language)} /> : translate("analyticsNotMeasured"),
                  links: <ConversationLinks conversations={suggestion.recent_conversations} popoverKey={`${context.context}/${suggestion.label}`} openKey={openKey} setOpenKey={setOpenKey} />,
                }))}
              />
            </div>
          );
        })}
        {nextOffset < suggestions.contexts_total && (
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="px-3 py-1.5 text-sm rounded-lg border disabled:opacity-50"
            style={{ ...card, color: "var(--color-text-primary)" }}
          >
            {translate("analyticsMoreContexts", { shown: contexts.length, total: suggestions.contexts_total })}
          </button>
        )}
        {moreError && (
          <p role="alert" className="text-sm" style={{ color: "var(--color-error-text)" }}>
            {apiErrorMessage(moreError, translate("analyticsLoadFailed"))}
          </p>
        )}
      </Section>
      <Section title={translate("analyticsTypedQuestions")} info={translate("analyticsTypedQuestionsNote", { count: typed.min_contacts })} className="space-y-2">
        {typed.limited_by_retention && (
          <p className="text-xs" style={secondary}>
            {typed.retention_days === 1 ? translate("analyticsLimitedByRetentionOneDay") : translate("analyticsLimitedByRetention", { days: typed.retention_days })}
          </p>
        )}
        {typed.items.length === 0 ? (
          <p className="text-sm" style={secondary}>
            {translate("analyticsNoTypedQuestions", { count: typed.min_contacts })}
          </p>
        ) : (
          <div className="rounded-xl border p-4 space-y-3" style={card}>
            <DataTable
              caption={translate("analyticsTypedQuestions")}
              columns={[
                { key: "text", label: translate("analyticsQuestion") },
                { key: "conversations", label: translate("analyticsConversationCount"), numeric: true },
                { key: "contacts", label: translate("analyticsContactCount"), numeric: true },
                ...links,
              ]}
              rows={typedItems.map((item) => ({
                key: item.text,
                text: item.text,
                conversations: <InlineBar value={item.conversation_count} max={typedMax} text={formatCount(item.conversation_count, language)} />,
                contacts: formatCount(item.contact_count, language),
                links: <ConversationLinks conversations={item.recent_conversations} popoverKey={`typed/${item.text}`} openKey={openKey} setOpenKey={setOpenKey} />,
              }))}
            />
            {typed.items.length > TYPED_SHOWN && (
              <button type="button" onClick={() => setAllTyped((shown) => !shown)} className="text-xs underline" style={{ color: "var(--color-text-secondary)" }}>
                {allTyped ? translate("analyticsShowFewerQuestions") : translate("analyticsShowAllQuestions", { count: typed.items.length })}
              </button>
            )}
          </div>
        )}
      </Section>
    </div>
  );
};

export default ContentTab;
